/**
 * The assistant's response contract — the single envelope every `POST /api/v1/chatbot/chat`
 * turn comes back in.
 *
 * The portal used to decide what to draw by looking for `<!--ATTENDANCE_JSON-->` inside the
 * answer TEXT. That marker is present on every successful attendance answer INCLUDING the
 * empty ones, so a "no attendance records found" reply still drew the report card, the chart
 * and the export controls over nothing. `presentation` replaces that guess: the backend now
 * states what the turn is, and the structured payload arrives in `data` rather than encoded
 * into the prose.
 *
 * See `chatbot-architecture.md` §4.7 (the response contract) and §4.9 (conversation state).
 */

/** What the backend says this turn is. Authoritative — never re-derive it from the prose. */
export type AssistantPresentation = 'text' | 'report' | 'steps' | 'clarify' | 'menu'

const PRESENTATIONS = new Set<string>(['text', 'report', 'steps', 'clarify', 'menu'])

/**
 * Why a clarification was asked, which decides how its options read:
 * - `slot` — one option per missing slot; the label names what to supply.
 * - `capability` — one option per capability the question could have meant.
 * - `menu` — the same list, offered as "here's what I can help with".
 */
export type AssistantClarificationKind = 'slot' | 'capability' | 'menu'

export interface AssistantClarificationOption {
  label: string
  /** A registered capability id (`attendance.data`, `support.manual`, …) when the option picks one. */
  capabilityId?: string | null
  description?: string | null
  /** The slot this option supplies, on `kind: 'slot'` clarifications. */
  slot?: string | null
}

export interface AssistantClarification {
  question?: string
  kind?: AssistantClarificationKind | string
  options: AssistantClarificationOption[]
  /**
   * The question THIS clarification was asked about — not the label of whichever chip gets
   * clicked. The backend is stateless per turn and attaches this on every clarification
   * (`router/clarify.js`) specifically so it can be echoed back; without it, answering a SECOND
   * clarification in the same conversation re-runs whatever question the FIRST one was about,
   * because the backend has nothing else to rebind the answer to.
   */
  query?: string
}

/**
 * A widening offer on a no-data answer — structured rather than a sentence because
 * `attendance-sql-agent/userText` forbids offer phrasing outright ("shall I…", "would you
 * like me to…"): an offer instead of an answer is a refusal wearing a helpful face. A chip is
 * one click instead of a round trip and keeps the prose offer-free.
 */
export interface AssistantSuggestion {
  label: string
  query: string
}

/**
 * One step of a `presentation: 'steps'` walkthrough, from `data.steps`.
 *
 * Prose and navigation are separate on purpose: `title`/`text` are what the reader is told,
 * while `path`/`target` are what the in-app walkthrough runner needs to drive the portal. A
 * step list with neither is still a perfectly good answer — it just cannot be replayed live.
 */
export interface AssistantStep {
  title?: string
  text?: string
  path?: string
  target?: string
  [key: string]: unknown
}

/** The steps of a `steps` turn, or an empty list. Accepts the `walkthrough` alias the
 *  walkthrough board has always used for the same array. */
/** What the chat endpoints answer with HTTP 429 (node-api chat rate limiter). */
export const CHAT_RATE_LIMIT_MESSAGE = 'Too many messages. Please wait a moment and try again.'

/**
 * The message to show for a rate-limited turn, or null when the failure is anything else. The
 * saga hands `onFailure` the parsed error body, which carries `statusCode` (and sometimes
 * `status`) — never the HTTP response itself.
 */
export function readRateLimitMessage(error: unknown): string | null {
  if (error == null || typeof error !== 'object') return null
  const record = error as Record<string, unknown>
  const limited = [record.statusCode, record.status, record.code].some(
    (value) => Number(value) === 429
  )
  if (!limited) return null
  const message = typeof record.message === 'string' ? record.message.trim() : ''
  return message || CHAT_RATE_LIMIT_MESSAGE
}

/**
 * What Versi says when a turn ran out of time: the backend's own turn deadline (HTTP 504 with a
 * JSON body), or the client's watchdog in `useAttendanceChat` when no reply arrived at all.
 */
export const CHAT_TURN_TIMEOUT_MESSAGE =
  'This is taking longer than expected. Please try again, or narrow the question — for example a shorter date range or a single class.'

/**
 * The message to show for a turn the backend abandoned at its deadline, or null for any other
 * failure. Only a JSON 504 reaches here — a proxy's own 504 carries no CORS headers and arrives as
 * a network error instead.
 */
export function readTurnTimeoutMessage(error: unknown): string | null {
  if (error == null || typeof error !== 'object') return null
  const record = error as Record<string, unknown>
  const timedOut = [record.statusCode, record.status, record.code].some(
    (value) => Number(value) === 504
  )
  if (!timedOut) return null
  const message = typeof record.message === 'string' ? record.message.trim() : ''
  return message || CHAT_TURN_TIMEOUT_MESSAGE
}

/** What Versi says when a school has no admission form (admission chat endpoints, HTTP 403). */
export const ADMISSION_FORM_DISABLED_MESSAGE = 'The admission form is not enabled for this school.'

/**
 * The reply for an admission chat turn refused because the school's admission form is off, or
 * null for any other failure. Only meaningful for the admission endpoints — a 403 from
 * `chatbot/chat` means something else.
 */
export function readAdmissionFormDisabledMessage(error: unknown): string | null {
  if (error == null || typeof error !== 'object') return null
  const record = error as Record<string, unknown>
  const forbidden = [record.statusCode, record.status, record.code].some(
    (value) => Number(value) === 403
  )
  return forbidden ? ADMISSION_FORM_DISABLED_MESSAGE : null
}

export function readAssistantSteps(data: Record<string, unknown> | null): AssistantStep[] {
  const raw = data?.steps ?? data?.walkthrough
  if (!Array.isArray(raw)) return []
  return raw
    .map((entry) => asRecord(entry))
    .filter((entry): entry is Record<string, unknown> => entry != null)
    .map((entry) => entry as AssistantStep)
    .filter((step) => Boolean(step.title || step.text || step.path || step.target))
}

/** True once a step list carries enough for the in-app runner to actually drive the portal. */
export function stepsAreNavigable(steps: AssistantStep[]): boolean {
  return steps.some((step) => typeof step.path === 'string' && step.path.trim().length > 0)
}

/** What the server understood a non-English question to be. Null when it was already English. */
export interface AssistantUnderstoodAs {
  query: string
  original: string
  dateRange?: {from: string; to: string} | null
  entities?: string[]
}

export interface AssistantResponse {
  answer: string
  /** `null` only when the backend predates the contract — see `useAttendanceChat`. */
  presentation: AssistantPresentation | null
  /** The structured payload. Only ever read when `presentation` says there is one. */
  data: Record<string, unknown> | null
  capability: string | null
  clarification: AssistantClarification | null
  suggestions: AssistantSuggestion[]
  emptyResult: boolean
  understoodAs: AssistantUnderstoodAs | null
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value != null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

function asNonEmptyString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null
}

/**
 * Every wrapper level the envelope can arrive at — the API layer hands back the raw body for
 * some calls and `{result: …}` / `{data: …}` for others, and `extractChatbotAnswer` already
 * searches the same four places for the answer text.
 */
function envelopeCandidates(response: unknown): Record<string, unknown>[] {
  const root = asRecord(response)
  if (!root) return []
  const result = asRecord(root.result)
  return [root, asRecord(root.data), result, result ? asRecord(result.data) : null].filter(
    (candidate): candidate is Record<string, unknown> => candidate != null
  )
}

/** The level that carries the contract: the one stating a `presentation`, else the one with prose. */
function findEnvelope(candidates: Record<string, unknown>[]): Record<string, unknown> | null {
  const byPresentation = candidates.find(
    (candidate) =>
      typeof candidate.presentation === 'string' && PRESENTATIONS.has(candidate.presentation)
  )
  if (byPresentation) return byPresentation
  return candidates.find((candidate) => asNonEmptyString(candidate.answer)) ?? candidates[0] ?? null
}

function firstFrom<T>(
  candidates: Record<string, unknown>[],
  key: string,
  read: (value: unknown) => T | null
): T | null {
  for (const candidate of candidates) {
    const value = read(candidate[key])
    if (value != null) return value
  }
  return null
}

function readClarification(value: unknown): AssistantClarification | null {
  const record = asRecord(value)
  if (!record) return null
  const options = Array.isArray(record.options) ? record.options : []
  const parsed = options
    .map((option) => asRecord(option))
    .filter((option): option is Record<string, unknown> => option != null)
    .map((option) => ({
      label: String(option.label ?? '').trim(),
      capabilityId: asNonEmptyString(option.capabilityId),
      description: asNonEmptyString(option.description),
      slot: asNonEmptyString(option.slot),
    }))
    .filter((option) => option.label.length > 0 || option.capabilityId != null)

  const question = asNonEmptyString(record.question)
  if (!parsed.length && !question) return null

  return {
    ...(question ? {question} : {}),
    ...(asNonEmptyString(record.kind) ? {kind: String(record.kind)} : {}),
    ...(asNonEmptyString(record.query) ? {query: String(record.query)} : {}),
    options: parsed,
  }
}

function readSuggestions(value: unknown): AssistantSuggestion[] {
  if (!Array.isArray(value)) return []
  return value
    .map((entry) => asRecord(entry))
    .filter((entry): entry is Record<string, unknown> => entry != null)
    .map((entry) => ({
      label: String(entry.label ?? '').trim(),
      query: String(entry.query ?? '').trim(),
    }))
    .filter((entry) => entry.label.length > 0 && entry.query.length > 0)
}

function readUnderstoodAs(value: unknown): AssistantUnderstoodAs | null {
  const record = asRecord(value)
  const query = record ? asNonEmptyString(record.query) : null
  if (!record || !query) return null
  return {
    query,
    original: typeof record.original === 'string' ? record.original : '',
    dateRange: (asRecord(record.dateRange) as AssistantUnderstoodAs['dateRange']) ?? null,
    entities: Array.isArray(record.entities) ? record.entities.map(String) : undefined,
  }
}

/**
 * Reads one chat response into the contract, wherever the API layer wrapped it.
 *
 * `data` is read ONLY when the envelope states a `presentation`: without one this is a
 * pre-contract backend, where `data` is just as likely to be the transport's own wrapper as
 * the answer's payload, and the caller falls back to the legacy marker path instead.
 */
export function extractAssistantResponse(response: unknown): AssistantResponse {
  const candidates = envelopeCandidates(response)
  const envelope = findEnvelope(candidates)

  const presentationRaw = envelope?.presentation
  const presentation =
    typeof presentationRaw === 'string' && PRESENTATIONS.has(presentationRaw)
      ? (presentationRaw as AssistantPresentation)
      : null

  // An empty attendance result states `emptyResult` and its widening `suggestions` under `meta`,
  // and the backend copies both to `metadata` as well — read whichever is present.
  const meta = envelope ? (asRecord(envelope.meta) ?? asRecord(envelope.metadata)) : null
  const metadata = envelope ? asRecord(envelope.metadata) : null

  return {
    answer: firstFrom(candidates, 'answer', asNonEmptyString) ?? '',
    presentation,
    data: presentation && envelope ? asRecord(envelope.data) : null,
    capability: envelope ? asNonEmptyString(envelope.capability) : null,
    clarification: firstFrom(candidates, 'clarification', readClarification),
    suggestions: readSuggestions(meta?.suggestions ?? metadata?.suggestions),
    emptyResult: meta?.emptyResult === true || metadata?.emptyResult === true,
    understoodAs: firstFrom(candidates, 'understoodAs', readUnderstoodAs),
  }
}

/**
 * The clarification exactly as it goes back on the next turn — `label`, `capabilityId`,
 * `description` and `slot`, and nothing else.
 *
 * The echo is what binds an answer to the question we asked: the backend is stateless per
 * turn, so without it "the second one" has no second one to be. It is also untrusted input on
 * the way back, which is why nothing is added to it here — no resolved ids, no scope, no
 * permissions. The backend discards those and re-probes anyway, and the round trip is not an
 * authorisation channel.
 */
export function sanitizeClarificationForEcho(
  clarification: AssistantClarification | null | undefined
): AssistantClarification | null {
  if (!clarification) return null
  return {
    ...(clarification.question ? {question: clarification.question} : {}),
    ...(clarification.kind ? {kind: clarification.kind} : {}),
    ...(clarification.query ? {query: clarification.query} : {}),
    options: clarification.options.map((option) => ({
      label: option.label,
      ...(option.capabilityId ? {capabilityId: option.capabilityId} : {}),
      ...(option.description ? {description: option.description} : {}),
      ...(option.slot ? {slot: option.slot} : {}),
    })),
  }
}

/** True for the two presentations that ask the user something and carry chips to answer with. */
export function isClarifyingPresentation(
  presentation: AssistantPresentation | null | undefined
): boolean {
  return presentation === 'clarify' || presentation === 'menu'
}
